import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoModalModule,
  PoModalComponent,
  PoModalAction
} from '@po-ui/ng-components';

import { CNPJ_MASK, CNPJ_PATTERN, isValidCnpj } from '../../../core/utils/validators';
import { RecursoRegra, RegraBase } from '../shared/regras-api.service';
import { RegrasListaBase } from '../shared/regras-lista.base';
import { RegrasSkeletonComponent, RegrasToolbarComponent } from '../shared/regras-toolbar';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** Tomador habilitado à aprovação sem processo (tabela ZOM). */
export interface RegraTomador extends RegraBase {
  cnpj: string;
  nome: string;
  tes: string;
  natureza: string;
}

/**
 * Aba de tomadores habilitados à aprovação sem processo (ZOM).
 *
 * Diferente das demais, a chave aqui é o próprio CNPJ — não há código
 * sequencial, e por isso o campo é desabilitado na alteração: mudá-lo seria
 * criar outro registro, não editar este.
 *
 * O cadastro substitui a comparação por raiz de CNPJ que existia no código
 * (grupo DAAS). Cada filial passa a ter sua linha: incluir uma filial nova
 * deixou de exigir liberação de programa e virou trabalho do usuário-chave.
 */
@Component({
  selector: 'app-regras-tomador',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule,
    RegrasToolbarComponent,
    RegrasSkeletonComponent,
    PoComboListaVisivelDirective
  ],
  templateUrl: './tomador.html',
  styles: [`
    .regras-auditoria { color: var(--color-neutral-dark-70, #6e6e6e); }
    ::ng-deep .regras-form .po-row { margin-bottom: 1.5rem; }
    ::ng-deep .regras-form .po-row:last-child { margin-bottom: 0; }
  `]
})
export class RegrasTomadorComponent extends RegrasListaBase<RegraTomador> {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  protected readonly recurso: RecursoRegra = 'tomador';
  protected readonly rotulo = 'os tomadores habilitados';

  private readonly fb = inject(FormBuilder);

  editing: RegraTomador | null = null;

  readonly formGroup: FormGroup = this.fb.group({
    cnpj: ['', Validators.required],
    nome: ['', Validators.required],
    // TES 403 e natureza 293014 são o par aplicado hoje na aprovação sem
    // processo; vêm preenchidos para poupar digitação, mas são editáveis.
    tes: ['403', Validators.required],
    natureza: ['293014', Validators.required],
    ativo: ['1', Validators.required]
  });

  readonly cnpjMask = CNPJ_MASK;
  readonly cnpjPattern = CNPJ_PATTERN;

  get modalTitle(): string {
    return this.editing ? 'Editar tomador' : 'Novo tomador';
  }

  readonly columns: Array<PoTableColumn> = [
    {
      property: 'cnpj', label: 'CNPJ', width: '190px', type: 'link',
      action: (_: string, row: RegraTomador) => this.editar(row)
    },
    { property: 'nome', label: 'Razão social' },
    { property: 'tes', label: 'TES', width: '100px' },
    { property: 'natureza', label: 'Natureza', width: '130px' },
    {
      property: 'ativo', label: 'Situação', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Ativo', color: 'color-10' },
        { value: '2', label: 'Inativo', color: 'color-07' }
      ]
    }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar', icon: 'an an-pencil-simple', action: (row: RegraTomador) => this.editar(row) },
    {
      label: 'Inativar', icon: 'an an-lock', type: 'danger',
      action: (row: RegraTomador) => this.alterarSituacao(row, '2'),
      visible: (row: RegraTomador) => row.ativo === '1'
    },
    {
      label: 'Reativar', icon: 'an an-lock-open',
      action: (row: RegraTomador) => this.alterarSituacao(row, '1'),
      visible: (row: RegraTomador) => row.ativo === '2'
    }
  ];

  readonly modalPrimaryAction: PoModalAction = { action: () => this.salvar(), label: 'Salvar' };
  readonly modalSecondaryAction: PoModalAction = { action: () => this.formModal.close(), label: 'Cancelar' };

  protected corpo(tomador: RegraTomador): Record<string, string> {
    return {
      // Só dígitos: a máscara é da tela, o campo no dicionário guarda o CNPJ
      // sem formatação.
      cnpj: (tomador.cnpj ?? '').replace(/\D/g, ''),
      nome: tomador.nome ?? '',
      tes: tomador.tes,
      natureza: tomador.natureza,
      ativo: tomador.ativo
    };
  }

  abrirNova(): void {
    this.editing = null;
    this.formGroup.reset(this.valoresPadrao());
    this.formGroup.get('cnpj')?.enable();
    this.formModal.open();
  }

  editar(tomador: RegraTomador): void {
    this.editing = tomador;
    this.formGroup.reset({ ...this.valoresPadrao(), ...tomador });
    // O CNPJ é a chave do cadastro: alterar seria criar outro registro.
    this.formGroup.get('cnpj')?.disable();
    this.formModal.open();
  }

  salvar(): void {
    // getRawValue inclui o CNPJ mesmo desabilitado na alteração.
    const tomador = this.formGroup.getRawValue() as RegraTomador;

    const erro = this.validar(tomador);
    if (erro) {
      this.notif.warning(erro);
      return;
    }

    this.gravar(tomador, !!this.editing, () => this.formModal.close());
  }

  private validar(f: RegraTomador): string {
    const cnpj = (f.cnpj ?? '').replace(/\D/g, '');
    if (!cnpj) { return 'Informe o CNPJ do tomador.'; }
    if (!isValidCnpj(cnpj)) { return 'CNPJ inválido. Verifique o número informado.'; }
    if (!f.nome?.trim()) { return 'Informe a razão social do tomador.'; }
    if (!f.tes) { return 'Informe o tipo de operação (TES).'; }
    if (!f.natureza) { return 'Informe a natureza financeira.'; }
    return '';
  }

  private valoresPadrao(): Partial<RegraTomador> {
    return { cnpj: '', nome: '', tes: '403', natureza: '293014', ativo: '1' };
  }
}
